import { useCallback, useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Button } from "@blueprint/ui";

import projectApi from "../../api/project";
import { Loader, Table } from "../../common/components/core";
import { useSelector } from "../../redux/hooks";
import { currentRolesSelector } from "../../redux/slices/auth/selectors";
import { PageTitle } from "../../types/pageTitle";
import { useProjectListResponse } from "../../types/project";
import { isUserPO } from "../../utils/role";

import ProjectList from "./components/ProjectList";
import { SearchIcon } from "../../common/components/core/icons";
import styled from "styled-components";
import useDebounce from "../../hooks/useDebounce";

type PaginationType = {
    page: number;
    pageSize: number;
    projectID: string;
};


const ProjectOverview = (): JSX.Element => {
    document.title = PageTitle.ProjectOverview;

    return <PureProjectOverview />;
};

export const PureProjectOverview = (): JSX.Element => {
    const currentRoles = useSelector(currentRolesSelector);
    const [paginationParams, setPaginationParams] = useState<PaginationType>({
        page: 1,
        pageSize: 9,
        projectID: "",
    });
    const searchValueDebounce = useDebounce(paginationParams.projectID, 1000);
    const [projectListPaging, setProjectListPaging] = useState<useProjectListResponse>();
    const [isValidating, setIsValidating] = useState(false);

    const nextPage = () => {
        if (projectListPaging && !projectListPaging.lastPage) {
            setPaginationParams((oldData) => {
                return {
                    ...oldData,
                    page: paginationParams.page + 1
                }
            })
        }
    };
    const previousPage = () => {
        if (paginationParams.page > 0) {
            setPaginationParams((oldData) => {
                return {
                    ...oldData,
                    page: paginationParams.page - 1
                }
            })
        }
    };

    const toPage = (page: number) => {
        if (projectListPaging && page !== projectListPaging.page) {
            if (page >= 0 && page <= projectListPaging.pageCount) {
                setPaginationParams((oldData) => {
                    return {
                        ...oldData,
                        page: page
                    }
                })
            }
        }
    };

    useEffect(() => {
        const getProjectList = async () => {
            try {
                setIsValidating(true);
                const projectListRes = await projectApi.getProjectsPaging(
                    currentRoles,
                    paginationParams.page,
                    paginationParams.pageSize,
                    paginationParams.projectID,
                );
                setProjectListPaging(projectListRes);
            } catch (e) {
                console.error(e);
            }
            setIsValidating(false);
        };

        getProjectList();
    }, [paginationParams.page, searchValueDebounce, paginationParams.pageSize, currentRoles]);
    const handleChangeSearchValue = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPaginationParams({
                ...paginationParams,
                projectID: e.target.value,
            });
        },
        [paginationParams],
    );

    return (
        <>
            <div className="d-flex align-items-center mb-2">
                <h1 className="flex-grow-1 mb-0">Projects</h1>
                {isUserPO(currentRoles) && (
                    <NavLink to="/project/new">
                        <Button variant="light.primary">New Project</Button>
                    </NavLink>
                )}
            </div>
            <Loader isLoading={isValidating}>
                {projectListPaging &&
                    <>
                        <div className="d-flex align-items-center justify-content-end">
                            <div className="w-75 d-flex justify-content-end gap-2">
                                <div className="w-50 d-flex ">
                                    <input
                                        placeholder="Search Project ID"
                                        value={paginationParams.projectID}
                                        onChange={handleChangeSearchValue}
                                        className="px-3 w-100 py-2"
                                    />
                                    <StyledButton>
                                        <SearchIcon />
                                    </StyledButton>
                                </div>
                                <StyledPagination>
                                    <Table.Pagination
                                        className="m-0"
                                        backArrowOnClick={previousPage}
                                        nextArrowOnClick={nextPage}
                                        backArrowDisabled={projectListPaging.firstPage}
                                        nextArrowDisabled={projectListPaging.lastPage}
                                        totalPage={projectListPaging.pageCount}
                                        currentPage={projectListPaging.page}
                                        onPageNumberChange={(page) => toPage(page)}
                                    />
                                </StyledPagination>
                            </div>
                        </div>
                        <ProjectList projects={projectListPaging.items ?? []} />
                    </>
                }
            </Loader>
        </>
    );
};

export default ProjectOverview;

const StyledButton = styled.button`
    background-color: transparent;
    border: none;
`;

const StyledPagination = styled.div`
    width: fit-content;
`;
